// ─────────────────────────────────────────────────────────────────────────
// TeamsRequiredModal.tsx — shows when a user tries to do something that
// requires Teams (Report Incident, etc.) but it's not configured. Fires
// on the `cima:teams-required` custom event from anywhere in the app.
//
// Two variants:
//   - Admin: CTA to jump straight to Settings → Integrations
//   - Staff: "Ask your admin to set this up" message + dismissable
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';

interface Props {
  isAdmin:           boolean;
  onOpenIntegrations: () => void;
}

export function TeamsRequiredModal({ isAdmin, onOpenIntegrations }: Props) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('cima:teams-required', handler);
    return () => window.removeEventListener('cima:teams-required', handler);
  }, []);

  // Also auto-show if a fetch returns the TEAMS_REQUIRED errorCode anywhere
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.code === 'TEAMS_REQUIRED') setOpen(true);
    };
    window.addEventListener('cima:api-error', handler);
    return () => window.removeEventListener('cima:api-error', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div onClick={() => setOpen(false)} style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        zIndex: 2000, backdropFilter: 'blur(2px)',
      }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        zIndex: 2001, width: 'min(460px, calc(100vw - 32px))',
        background: 'var(--card)', border: '1px solid var(--border-md)',
        borderRadius: 16, padding: 24,
        boxShadow: '0 24px 64px rgba(15,23,42,0.32)',
        animation: 'cimaTeamsReqIn 0.2s ease-out',
      }}>
        <style>{`@keyframes cimaTeamsReqIn { from { opacity: 0; transform: translate(-50%,-46%) scale(0.97); } to { opacity: 1; transform: translate(-50%,-50%) scale(1); } }`}</style>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
        }}>
          <span style={{
            width: 40, height: 40, borderRadius: 11,
            background: 'var(--primary)',
            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20,
          }}>📞</span>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
            Microsoft Teams isn't connected
          </h3>
        </div>

        <p style={{
          margin: '0 0 18px', fontSize: 13.5, lineHeight: 1.65, color: 'var(--fg-soft)',
        }}>
          {isAdmin ? (
            <>
              CIMA needs your organisation's Microsoft Teams to schedule meetings and pull transcripts. <strong style={{ color: 'var(--fg)' }}>Until Teams is connected, no new incidents can be raised.</strong> Takes ~15 minutes with your Azure admin.
            </>
          ) : (
            <>
              Your platform admin still needs to connect Microsoft Teams. <strong style={{ color: 'var(--fg)' }}>Until they do, new incidents can't be raised in CIMA.</strong> You can still browse existing incidents, complete training, and use the Crisis Advisor in the meantime.
            </>
          )}
        </p>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={() => setOpen(false)} style={{
            background: 'var(--card)', color: 'var(--fg)',
            border: '1px solid var(--border-md)', borderRadius: 10,
            padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            fontFamily: 'inherit',
          }}>
            {isAdmin ? 'Not now' : 'Got it'}
          </button>
          {isAdmin && (
            <button onClick={() => { setOpen(false); onOpenIntegrations(); }} style={{
              background: 'var(--primary)',
              color: 'white', border: 'none', borderRadius: 10,
              padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 2px 8px rgba(99,55,138,0.28)',
            }}>
              Set up Teams →
            </button>
          )}
        </div>
      </div>
    </>
  );
}
